import React, { useEffect, useMemo, useState } from "react";
import "./performance.css";
import StrategyPerformanceOverview from "./StrategyPerformanceOverview";

const periods = ["1m", "3m", "6m", "1y"];
const money = value => new Intl.NumberFormat("en-IN", {
  style: "currency", currency: "INR", maximumFractionDigits: 2
}).format(value || 0);
const when = value => value ? new Date(value).toLocaleString("en-IN") : "—";
const strategyName = value => String(value || "Legacy")
  .replace("NIFTY_EMA_FLIP", "Index Options · NIFTY EMA Flip")
  .replace("SENSEX_EMA_FLIP", "Index Options · SENSEX EMA Flip")
  .replace("STOCK_DERIVATIVES", "Equity Options")
  .replace("EQUITY_SWING", "Equity Swing")
  .replace("AUTO_STOCK_", "Equity Intraday · ")
  .replace("AUTO_INDEX_", "Index Options · ")
  .replace("INDEXOPTION_GAPSYNC_", "Index Options Legacy · ")
  .replace("INDEXOPTION_SYNC_", "Index Options · ")
  .replaceAll("_", " ");
const isStockTrade = trade => String(trade.product_id || "").startsWith("AUTO_STOCK");
const qualityName = value => String(value || "UNKNOWN").replaceAll("_", " ");

function groupTrades(trades) {
  const grouped = new Map();
  trades.forEach(trade => {
    const key = `${trade.product_id || "LEGACY"}::${trade.symbol || "UNKNOWN"}`;
    if (!grouped.has(key)) grouped.set(key, {
      key,
      productId: trade.product_id,
      symbol: trade.symbol || "Unknown instrument",
      type: isStockTrade(trade) ? "stock" : "option",
      trades: [],
    });
    grouped.get(key).trades.push(trade);
  });
  return [...grouped.values()].map(group => {
    const quantity = group.trades.reduce((sum, trade) => sum + Math.abs(Number(trade.qty) || 0), 0);
    const weighted = field => quantity ? group.trades.reduce(
      (sum, trade) => sum + (Number(trade[field]) || 0) * Math.abs(Number(trade.qty) || 0), 0
    ) / quantity : 0;
    const statuses = [...new Set(group.trades.map(trade => qualityName(trade.data_status)))];
    return {
      ...group,
      clients: new Set(group.trades.map(trade => trade.client)).size,
      quantity,
      averageEntry: weighted("entry_price"),
      averageExit: weighted("exit_price"),
      averageReturn: weighted("return_pct"),
      gross: group.trades.reduce((sum, trade) => sum + (Number(trade.gross_pnl) || 0), 0),
      costs: group.trades.reduce((sum, trade) => sum + (Number(trade.costs) || 0), 0),
      net: group.trades.reduce((sum, trade) => sum + (Number(trade.net_pnl) || 0), 0),
      issues: group.trades.filter(trade => !String(trade.data_status || "").toUpperCase().includes("VERIFIED")).length,
      statuses,
      latestExit: group.trades.reduce((latest, trade) => String(trade.exit_at || "") > latest ? String(trade.exit_at || "") : latest, ""),
    };
  });
}

function Chart({ title, points = [], formatValue = value => String(value), summary = null }) {
  if (points.length < 2) {
    const latest = points.at(-1);
    return <article className="pf-chart compact"><strong>{title}</strong><div className="pf-chart-summary"><b>{latest ? formatValue(latest.value) : "—"}</b><span>{latest ? summary || "Trend graph starts after two trading days." : "No finalized live trades."}</span></div></article>;
  }
  const values = points.map(point => Number(point.value));
  const low = Math.min(...values), spread = Math.max(...values) - low || 1;
  const path = points.map((point, index) => `${index ? "L" : "M"}${points.length === 1 ? 50 : index * 100 / (points.length - 1)},${94 - (point.value - low) * 84 / spread}`).join(" ");
  return <article className="pf-chart"><strong>{title}</strong><svg viewBox="0 0 100 100" preserveAspectRatio="none"><path d={path} /></svg><small>{points[0].date}<b>{formatValue(points.at(-1).value)}{summary ? ` · ${summary}` : ""}</b>{points.at(-1).date}</small></article>;
}

export default function PerformanceDashboard({ clients = [], onSessionExpired, instances = {}, positions = [], orders = [], stock = {}, active = true, connected = true }) {
  const [products, setProducts] = useState([]), [productId, setProductId] = useState("");
  const [period, setPeriod] = useState("1m"), [view, setView] = useState("aggregate"), [clientId, setClientId] = useState("");
  const [report, setReport] = useState(null), [message, setMessage] = useState("Loading performance…");
  const [expandedGroups, setExpandedGroups] = useState({}), [tradeSearch, setTradeSearch] = useState("");
  const [tradeType, setTradeType] = useState("all"), [tradeSort, setTradeSort] = useState("latest");

  useEffect(() => {
    fetch("/api/performance/catalog", { cache: "no-store" })
      .then(response => {
        if (response.status === 401) onSessionExpired?.();
        if (!response.ok) throw Error("Unable to load performance");
        return response.json();
      })
      .then(data => {
        setProducts(data.products || []);
        setProductId(data.products?.[0]?.id || "");
        setMessage(data.products?.length ? "" : "Verified performance begins with the next finalized live entry and exit.");
      })
      .catch(error => setMessage(error.message));
  }, [onSessionExpired]);

  useEffect(() => {
    if (!productId || (view === "client" && !clientId)) {
      setReport(null);
      return;
    }
    const query = new URLSearchParams({ product_id: productId, period, view });
    if (view === "client") query.set("client_id", clientId);
    const controller = new AbortController();
    setReport(null);
    fetch(`/api/performance/report?${query}`, { cache: "no-store", signal: controller.signal })
      .then(response => response.ok ? response.json() : Promise.reject(Error("Unable to load report")))
      .then(data => { if (!controller.signal.aborted) { setReport(data); setMessage(""); } })
      .catch(error => { if (!controller.signal.aborted) setMessage(error.message); });
    return () => controller.abort();
  }, [productId, period, view, clientId]);

  const product = products.find(item => item.id === productId);
  const metrics = report?.metrics || {};
  const visibleTrades = [...(report?.trades || [])]
    .sort((left, right) => String(right.exit_at || "").localeCompare(String(left.exit_at || "")));
  const tradeGroups = useMemo(() => {
    const query = tradeSearch.trim().toLowerCase();
    const groups = groupTrades(visibleTrades).filter(group => {
      const typeMatches = tradeType === "all" || group.type === tradeType;
      const searchMatches = !query
        || group.symbol.toLowerCase().includes(query)
        || strategyName(group.productId).toLowerCase().includes(query)
        || group.trades.some(trade => String(trade.client || "").toLowerCase().includes(query));
      return typeMatches && searchMatches;
    });
    return groups.sort((left, right) => {
      if (left.issues !== right.issues) return right.issues - left.issues;
      if (tradeSort === "highest") return right.net - left.net;
      if (tradeSort === "clients") return right.clients - left.clients;
      if (tradeSort === "latest") return right.latestExit.localeCompare(left.latestExit);
      return left.net - right.net;
    });
  }, [visibleTrades, tradeSearch, tradeType, tradeSort]);
  const toggleGroup = key => setExpandedGroups(current => ({ ...current, [key]: !current[key] }));

  return <main className="pf-page">
    <header className="pf-toolbar">
      <div><h2>Performance</h2><p>All strategies · Paper and Live overview · finalized Live history</p></div>
      <select aria-label="Finalized Live strategy" value={productId} onChange={event => setProductId(event.target.value)}>
        <option value="">No history yet</option>
        {products.map(item => <option key={item.id} value={item.id}>{item.source === "AUTO_STOCK" ? "Equity Intraday: All trades" : item.source === "STOCK_DERIVATIVES" ? `Equity Options: ${item.underlying}` : ["NIFTY_EMA_FLIP", "SENSEX_EMA_FLIP", "ALGO"].includes(item.source) ? `Index Options: ${item.label}` : item.label}</option>)}
        <option value="ALL">All finalized Live ledger strategies</option>
      </select>
      <div className="pf-periods">{periods.map(item => <button key={item} className={period === item ? "active" : ""} onClick={() => setPeriod(item)}>{item.toUpperCase()}</button>)}</div>
      <select value={view} onChange={event => setView(event.target.value)}><option value="aggregate">Client / prospect preview</option><option value="admin">Admin detail</option><option value="client">Individual client</option></select>
      {view === "client" && <select value={clientId} onChange={event => setClientId(event.target.value)}><option value="">Select client</option>{clients.map(client => <option key={client.id} value={client.id}>{client.name || client.id}</option>)}</select>}
    </header>
    <StrategyPerformanceOverview instances={instances} positions={positions} orders={orders} stock={stock} clients={clients} clientId={view === "client" ? clientId : null} period={period} active={active} connected={connected} />
    <h2>Finalized Live performance</h2><p className="pf-overview-note">The charts below use finalized Live broker fills from the performance ledger. Paper results and Swing basket valuations above remain separate from these returns.</p>
    {message && <div className="pf-notice">{message}</div>}
    {product && !product.available_periods[period] && <div className="pf-notice">Finalized Live history does not yet cover the full {period.toUpperCase()} period. Official start: {product.official_start}.</div>}
    {report && <>
      <div className="pf-quality"><b>{report.quality.replaceAll("_", " ")}</b><span>As of {when(report.as_of)}</span></div>
      <section className="pf-kpis">{[["Normalized return", `${metrics.normalized_return_pct}%`], ["Max drawdown", `${metrics.max_drawdown_pct}%`], ["Win rate", `${metrics.win_rate_pct}%`], ["Profit factor", metrics.profit_factor ?? "—"], ["Finalized trades", metrics.trade_count], ["Clients", metrics.clients]].map(([label, value]) => <article key={label}><span>{label}</span><strong>{value}</strong></article>)}</section>
      <section className="pf-charts">
        <Chart
          title="Normalized Growth of ₹100"
          points={report.equity_curve}
          formatValue={value => `₹${Number(value || 0).toFixed(2)}`}
          summary={`Return ${Number(metrics.normalized_return_pct || 0) >= 0 ? "+" : ""}${Number(metrics.normalized_return_pct || 0).toFixed(2)}%`}
        />
        <Chart
          title="Drawdown (%)"
          points={report.drawdown}
          formatValue={value => `${Number(value || 0).toFixed(2)}%`}
          summary={`Maximum ${Number(metrics.max_drawdown_pct || 0).toFixed(2)}%`}
        />
      </section>
      <p className="pf-method"><b>Method:</b> {report.methodology} Broker executions: {report.coverage.attempts || 0}; verified live fills: {report.coverage.eligible_fills || 0}; failed executions: {report.coverage.failures || 0}. Historical verified fills remain stored for the selected performance period.</p>
      <section className="pf-history">
        <div className="pf-history-heading">
          <div><h3>Finalized Live trade history</h3><small>{tradeGroups.length} instrument groups · {visibleTrades.length} client trades</small></div>
          <div className="pf-history-controls">
            <input aria-label="Search trades" placeholder="Search instrument or client" value={tradeSearch} onChange={event => setTradeSearch(event.target.value)} />
            <select aria-label="Instrument type" value={tradeType} onChange={event => setTradeType(event.target.value)}><option value="all">All instruments</option><option value="stock">Stocks</option><option value="option">Options</option></select>
            <select aria-label="Sort instrument groups" value={tradeSort} onChange={event => setTradeSort(event.target.value)}><option value="latest">Latest trade first</option><option value="lowest">Lowest P&amp;L first</option><option value="highest">Highest P&amp;L first</option><option value="clients">Most clients</option></select>
          </div>
        </div>
        <div><table className="pf-group-table">
          <thead><tr><th aria-label="Expand" /><th>Instrument</th><th>Strategy</th><th>Clients</th><th>Trades</th><th>Qty</th><th>Avg Entry → Exit</th><th>Gross</th><th>Costs</th><th>Net P&amp;L</th><th>Avg Return</th><th>Quality</th></tr></thead>
          <tbody>{tradeGroups.length ? tradeGroups.flatMap(group => {
            const expanded = Boolean(expandedGroups[group.key]);
            const rows = [<tr className={`pf-group-row ${group.issues ? "has-issue" : ""}`} key={group.key}>
              <td><button className="pf-expand" aria-expanded={expanded} aria-label={`${expanded ? "Collapse" : "Expand"} ${group.symbol}`} onClick={() => toggleGroup(group.key)}>{expanded ? "▾" : "▸"}</button></td>
              <td><strong>{group.symbol}</strong><small>{group.type === "stock" ? "Stock" : "Option contract"}</small></td><td>{strategyName(group.productId)}</td><td>{group.clients}</td><td>{group.trades.length}</td><td>{group.quantity}</td><td>₹{group.averageEntry.toFixed(2)} → ₹{group.averageExit.toFixed(2)}</td><td>{money(group.gross)}</td><td>{money(group.costs)}</td><td className={group.net >= 0 ? "gain" : "loss"}><strong>{money(group.net)}</strong></td><td>{group.averageReturn.toFixed(2)}%</td><td>{group.issues ? <span className="pf-issue">{group.issues} issue{group.issues === 1 ? "" : "s"}</span> : group.statuses.join(", ")}</td>
            </tr>];
            if (expanded) rows.push(<tr className="pf-detail-row" key={`${group.key}-details`}><td colSpan="12"><div className="pf-detail-wrap"><table><thead><tr><th>Client</th><th>Entry Time</th><th>Exit Time</th><th>Qty</th><th>Entry → Exit</th><th>Gross</th><th>Costs</th><th>Net P&amp;L</th><th>Return</th><th>Quality</th></tr></thead><tbody>{group.trades.map((trade, index) => <tr key={`${group.key}-${trade.client}-${trade.exit_at}-${index}`}><td>{trade.client}</td><td>{when(trade.entry_at)}</td><td>{when(trade.exit_at)}</td><td>{trade.qty}</td><td>₹{trade.entry_price} → ₹{trade.exit_price}</td><td>{money(trade.gross_pnl)}</td><td>{money(trade.costs)}</td><td className={trade.net_pnl >= 0 ? "gain" : "loss"}>{money(trade.net_pnl)}</td><td>{trade.return_pct}%</td><td>{qualityName(trade.data_status)}</td></tr>)}</tbody></table></div></td></tr>);
            return rows;
          }) : <tr><td colSpan="12" className="pf-history-empty">{visibleTrades.length ? "No trades match the selected filters" : "No finalized Live trades in this period"}</td></tr>}</tbody>
        </table></div>
      </section>
    </>}
  </main>;
}
